import React, { useRef, useState, useEffect } from 'react';
import { FaPlay, FaPause } from 'react-icons/fa';

const AudioPlayer = ({ src }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [barHeights, setBarHeights] = useState(Array(12).fill(20));

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  // Animate bars when playing
  useEffect(() => {
    if (!isPlaying) {
      setBarHeights(Array(12).fill(20));
      return;
    }
    const interval = setInterval(() => {
      setBarHeights(prev => prev.map(() => Math.random() * 60 + 20));
    }, 120);
    return () => clearInterval(interval);
  }, [isPlaying]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    audio.currentTime = pct * duration;
  };

  const formatTime = (t) => {
    if (!t || isNaN(t)) return '0:00';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="chatdock-audio-player">
      <audio ref={audioRef} src={src} preload="metadata" />
      
      {/* Visualization area */}
      <div className="audio-visualizer">
        {barHeights.map((h, i) => (
          <div
            key={i}
            className="visualizer-bar"
            style={{
              height: `${h}%`,
              transition: isPlaying ? 'height 0.1s ease' : 'height 0.3s ease'
            }}
          />
        ))}
      </div>

      {/* Controls */}
      <div className="audio-controls">
        <button className="audio-play-btn" onClick={togglePlay} type="button" aria-label={isPlaying ? 'Pause' : 'Play'}>
          {isPlaying ? '⏸' : '▶'}
        </button>
        <span className="audio-time">{formatTime(currentTime)}</span>
        <div className="audio-progress" onClick={handleSeek}>
          <div className="audio-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <span className="audio-time">{formatTime(duration)}</span>
      </div>
    </div>
  );
};

export default AudioPlayer;
